/**
 * 🎣 **THE FISHING ROD — AND IT CATCHES A REAL FISH. D129, 2026-09-29.**
 *
 * 🎯 **haugur chose every rule from one table:**
 *
 * | | His choice |
 * |---|---|
 * | 🎯 Cast | right button, the float lands where you aim, **up to 10 m**, on water |
 * | 👀 Notice | a fish **within 8 m** takes an interest after **3–15 s**, and you SEE it come |
 * | 🫧 Bite | the float dips; **1.5 s** to pull |
 * | ❌ Miss | the fish **bolts**; only time is lost |
 * | 🌊 Empty | after **30 s** with no fish near, the game says so |
 * | 💨 Wind | the wait grows with it; **from 17 m/s nothing bites** |
 * | 🎣 Rod | **150 catches**, worn only by a fish landed |
 *
 * 📖 **`Minecraft/17`**: the rod and the fish are separate systems there — a cast
 * rolls a loot table. 🔴 **Ours are real (D117)**, so the float's life is a
 * small machine over REAL fish, and {@link tendFloat} is all of it: pure, so a
 * test reaches every beat without a server.
 *
 * ## The three beats, which Minecraft calls the whole minigame
 *
 * **wait** (`interestAt`) → **a visible approach** (a fish is `lured` and swims
 * to the float, drawn like any fish) → **a short window** (`biteAt` + 1.5 s).
 */

import type { Behaviour, Creature, Sense, Vec3 } from './creature.ts';

/** 🎣 The rod. */
export const ROD_ITEM = 'veidistong';
/** 🦴 A bone — a killed sheep gives one, three in the autumn window. */
export const BONE_ITEM = 'bein';

/** 🎯 How far a cast reaches, metres. */
export const CAST_REACH = 10;
/** 👀 How far from the float a fish may be to notice it, metres. */
export const LURE_RANGE = 8;
/** ⏱️ The wait before a fish takes an interest, REAL seconds — before wind. */
export const INTEREST_MIN_REAL_SECONDS = 3;
export const INTEREST_MAX_REAL_SECONDS = 15;
/** 🫧 How long the float is under, REAL seconds — the only skill in it. */
export const BITE_WINDOW_REAL_SECONDS = 1.5;
/** 🌊 How long with no fish near before the game says so, REAL seconds. */
export const NOTHING_NEAR_REAL_SECONDS = 30;
/** 💨 **From this mean wind, m/s, nothing bites.** ⚠️ The hvassviðri–stormur line, from memory. */
export const NO_BITE_WIND = 17;
/** 🫧 How close to the float counts as biting, metres, on the flat. */
export const BITE_DISTANCE = 0.8;
/** 🐟 How far below the surface a lured fish aims, metres — it must stay in water. */
export const LURE_DEPTH = 0.6;

/** The wait multiplier for a wind, m/s: calm is ×1, 10 m/s is ×2. */
export function windStretch(wind: number): number {
  return 1 + Math.max(0, wind) / 10;
}

/** 🎲 When the next fish takes an interest, real seconds from `now`. */
export function rollInterest(now: number, wind: number, rng: () => number): number {
  const span = INTEREST_MAX_REAL_SECONDS - INTEREST_MIN_REAL_SECONDS;
  return now + (INTEREST_MIN_REAL_SECONDS + rng() * span) * windStretch(wind);
}

/** 🎣 One player's float in the water. */
export interface FloatState {
  /** Where it floats, world metres, on the water's surface. */
  readonly at: Vec3;
  /** When it was cast, real seconds. */
  readonly castAt: number;
  /** When the next fish takes an interest, real seconds. */
  interestAt: number;
  /** The fish swimming to it, or `null`. */
  fishId: string | null;
  /** When the float went under, real seconds, or `null`. */
  biteAt: number | null;
  /** Has any fish ever come — so "nothing near" is said once, and only when true. */
  anyCame: boolean;
  /** Has the "nothing is biting" notice been given for this cast. */
  told: boolean;
}

export function newFloat(at: Vec3, now: number, wind: number, rng: () => number): FloatState {
  return {
    at,
    castAt: now,
    interestAt: rollInterest(now, wind, rng),
    fishId: null,
    biteAt: null,
    anyCame: false,
    told: false,
  };
}

/** A fish as the float machine sees it. */
export interface FishNear {
  readonly id: string;
  readonly at: Vec3;
}

/** What one beat of the float said happened — the server acts on it. */
export type FloatEvent =
  | { readonly kind: 'lured'; readonly fishId: string }
  | { readonly kind: 'bite'; readonly fishId: string }
  | { readonly kind: 'missed'; readonly fishId: string }
  | { readonly kind: 'lost'; readonly fishId: string }
  | { readonly kind: 'nothingNear' }
  | { readonly kind: 'tooWindy' };

function flat(a: Vec3, b: Vec3): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

/**
 * 🎣 **One beat of a float's life.** Mutates `f`; returns what happened.
 *
 * 🔴 **The order is the design**: a bite past its window is a MISS before
 * anything else is asked; a fish that has vanished lets the float wait again;
 * a fish that has arrived BITES; and only an idle float looks for a new fish.
 *
 * @param fish every fish within reach of the float, this beat
 * @param wind the mean wind at the float, m/s — `0` when unknown
 */
export function tendFloat(
  f: FloatState,
  fish: readonly FishNear[],
  now: number,
  wind: number,
  rng: () => number,
): FloatEvent[] {
  const events: FloatEvent[] = [];

  if (f.biteAt !== null && f.fishId !== null) {
    if (now - f.biteAt > BITE_WINDOW_REAL_SECONDS) {
      events.push({ kind: 'missed', fishId: f.fishId });
      f.fishId = null;
      f.biteAt = null;
      f.interestAt = rollInterest(now, wind, rng);
    }
    return events;
  }

  if (f.fishId !== null) {
    const coming = fish.find((one) => one.id === f.fishId);
    if (coming === undefined) {
      events.push({ kind: 'lost', fishId: f.fishId });
      f.fishId = null;
      f.interestAt = rollInterest(now, wind, rng);
      return events;
    }
    if (flat(coming.at, f.at) <= BITE_DISTANCE) {
      f.biteAt = now;
      events.push({ kind: 'bite', fishId: coming.id });
    }
    return events;
  }

  if (now < f.interestAt) return events;
  if (wind >= NO_BITE_WIND) {
    if (!f.told && now - f.castAt >= NOTHING_NEAR_REAL_SECONDS) {
      f.told = true;
      events.push({ kind: 'tooWindy' });
    }
    f.interestAt = now + 2;
    return events;
  }
  let best: FishNear | undefined;
  let bestD = Infinity;
  for (const one of fish) {
    const d = flat(one.at, f.at);
    if (d <= LURE_RANGE && d < bestD) {
      best = one;
      bestD = d;
    }
  }
  if (best === undefined) {
    if (!f.anyCame && !f.told && now - f.castAt >= NOTHING_NEAR_REAL_SECONDS) {
      f.told = true;
      events.push({ kind: 'nothingNear' });
    }
    f.interestAt = now + 1;
    return events;
  }
  f.fishId = best.id;
  f.anyCame = true;
  events.push({ kind: 'lured', fishId: best.id });
  return events;
}

/** 🫧 Is a pull right now inside the window? */
export function pullLands(f: FloatState, now: number): boolean {
  return f.biteAt !== null && f.fishId !== null && now - f.biteAt <= BITE_WINDOW_REAL_SECONDS;
}

/**
 * 🐟 **Lured** — swim to the float and take the bait. D129.
 *
 * 🔴 **Above `Follow` and below `Wary`**: a lured fish leaves its shoal's slot,
 * but a person wading in still scares it off. ⚠️ **It aims a little BELOW the
 * surface** (`LURE_DEPTH`), because a fish's `fits` refuses anything out of the
 * water.
 */
export function lured(priority: number): Behaviour {
  return {
    name: 'Lured',
    priority,
    flags: ['move'],
    wants: (sense: Sense) => (sense.lure ?? null) !== null,
    act(sense: Sense) {
      const lure = sense.lure;
      if (lure === undefined || lure === null) return {};
      return { goal: { x: lure.x, y: lure.y - LURE_DEPTH, z: lure.z }, pace: 1.2 };
    },
  };
}

/** 🐟 Scare a fish off a missed bite: the knife's `panic`, from the float. */
export function scareFrom(fish: Creature, from: Vec3, now: number): void {
  fish.memory.struckAt = now;
  fish.memory.fromX = from.x;
  fish.memory.fromZ = from.z;
}
